// lib/stores/global-store.ts
import { FlowTask } from '@/app/(work)/layout-left/left-data/novel.type';
import { nanoid } from 'nanoid';
import { User } from 'next-auth';
// import { User } from '@/lib/db/schema';
import { JSONContent } from 'novel';
import { TaskList } from 'novel/extensions';
import { toast } from 'sonner';
import { create } from 'zustand';
import { devtools, persist, PersistStorage, StorageValue } from 'zustand/middleware';
import { JsonBlock } from '@/components/zustom/type-prompt';
import { ReactNode } from 'react';
import { sslog } from '@/lib/utils/sslog';
import { NavLink, TaskType, Task, TaskStatus, Price } from '@/lib/type';
import { Message } from 'ai';


// 新增自定义存储类型适配器
// 1. 调整自定义存储类型定义
const createJSONStorage = <T>(): PersistStorage<T> => ({
  getItem: (name): StorageValue<T> | null => {
    const str = localStorage.getItem(name)
    return str ? { state: JSON.parse(str), version: 0 } : null
  },
  setItem: (name, value) => {
    localStorage.setItem(name, JSON.stringify(value.state))
  },
  removeItem: (name) => {
    localStorage.removeItem(name)
  }
})


export type Notification = {
  id: string;
  message: string;
};

export type ProcessEnv = {
  online?: boolean;
  template_editable?: boolean;
  fileupload?: boolean;
  inspire_enable?: boolean;
  deviceId?: string;
  licenseKey?: string;
  self_aikey?:boolean;
  selectedModel?:string;
  price?: Price;
}

interface GlobalState{
    processEnv: ProcessEnv,
    enabled:boolean;
    theme: 'light' | 'dark';
    user: User;
    // deviceId: string;
    toolActionName: string;
    mouseOverPath: string;
    selectedPath: string;
    selectedText: string;
    selectedChat: string;
    aiSuggestions: Record<string, Array<JSONContent | string>>  | null;
    
    inspireResult: JSONContent | string | null;
    activeToolItem: FlowTask;
    workflow: string;
    autoChat: boolean;
    fileChat: boolean;
    isMultiple: boolean;
    jsonBlock: JsonBlock | null;
    messageSugguestionJson: JSONContent | string | null;
    messagesMode: string;
    loading: boolean;
    notification: string;
    notifications: Notification[];

    // --- 任务队列状态更新 ---
    // 旧的 newTaskList 将被下面两个队列取代
    sequentialTasks: Task[]; // 存储必须按顺序执行的任务
    parallelTasks: Task[];   // 存储可以并行执行的任务
    // --- 旧的任务管理 (建议废弃或移除) ---
    newTask: Task | null;
    newTaskList: Task[];

    aiKeys: Record<string, string>;
    currentLink: NavLink | null;
    lastMessage: Message | null;       // 只保留尾节点
    ingestLastMessage: (m: Message) => void;
    setCurrentLink:(currentLink:NavLink) => void;
    setAiKeys: (aiKeys:Record<string, string>) => void;
    setProcessEnv: (processEnv:ProcessEnv) => void;
    updataProcessEnv: (patch: Partial<ProcessEnv>) => void;   // ← 重点在这里
    setUser: (user:User) => void;
    setAutoChat: (autoChat:boolean) =>void;
    setFileChat: (fileChat:boolean) =>void;
    setIsMultiple: (isMultiple:boolean) =>void;
    clear: ()=> void;
    toggleTheme: () => void;
    setMouseOverPath: (path: string) => void;
    setSelectedPath: (path: string) => void;
    SetSelectedText: (selectedText: string) => void;
    setSelectedChat: (selectedChat: string) => void;
    setInspireResult: (inspireResult:  JSONContent | string | null) => void;
    setToolActionName: (name: string) => void;
    setActiveToolItem: (acitiveItem: FlowTask) => void;
    setWorkflow: (workflow: string) => void;
    setMessagesMode:(messagesMode: string) => void;   
    setJsonBlock: (jsonBlock: JsonBlock) => void;
    setMessageSugguestionJson: (messageSugguestionJson: JSONContent|string|null)=>void;
    
    llog: (message: string) => void;

    // --- 新增/修改的任务管理 Actions ---
    addTask: (task: Omit<Task, 'id' | 'status' | 'createdAt'>) => void; // 统一的任务添加入口
    updateParallelTask: (taskId: string, updates: Partial<Task>) => void; // 更新并行任务状态
    removeParallelTask: (taskId: string) => void; // 移除已完成或失败的并行任务

    // 用于顺序任务执行器
    getFirstSequentialTask: () => Task | undefined;
    removeFirstSequentialTask: () => void;
    // 新增：用于手动拖拽排序顺序任务队列
    updateSequentialTaskOrder: (orderedTasks: Task[]) => void;

    // --- 旧的任务管理 Actions (建议废弃或移除) ---
    
    addTasks: (tasks: Task[]) => void;
    removeTask: (taskId: string) => void; 
    updateTasks: (tasks: Task[]) => void;
    setNewTaskList: (tasks: Task[]) => void;
    addNewTask: (task: Task) => void;
    updateNewTaskListOrder: (order: {name:string, id:string, title?:string, index:number}[]) => void;
};

const useGlobalStore = create<GlobalState>()( 
  devtools(persist<GlobalState, [], [], Partial<GlobalState>>((set, get) => ({
    processEnv: {online: false, template_editable: false},
    enabled: true,
    theme: 'light',
    user: {id:'', email:'', type: 'regular'},
    toolActionName: 'novel',
    mouseOverPath: '',
    selectedPath: '',
    selectedText: '',
    selectedChat: '',
    aiSuggestions: null,
    inspireResult: null,
    activeToolItem: {label:'', value:'', url:'', name:''},
    workflow: '',
    autoChat: true,
    fileChat: false,
    isMultiple: true,
    jsonBlock: null,
    messageSugguestionJson:  null,
    messagesMode: 'Grid',//  'List',
    loading: true,
    notification: '',
    notifications: [],

    // --- 新的任务队列状态 ---
    sequentialTasks: [],
    parallelTasks: [],
    // --- 旧的任务队列状态 ---
    newTask: null,
    newTaskList: [],
    aiKeys: {},
    currentLink: null,
    lastMessage: null,       // 只保留尾节点
    ingestLastMessage: (m: Message) => {set({ lastMessage: m })},
    setCurrentLink:(currentLink:NavLink) => {set({currentLink})},
    setAiKeys: (aiKeys:Record<string, string>) => {set({aiKeys})},
    setProcessEnv: (processEnv:ProcessEnv) => {set({processEnv})},
    // 局部更新：旧状态 + 新补丁
    updataProcessEnv: (patch) => set((state) => ({ processEnv: { ...state.processEnv, ...patch } })),
    setUser: (user:User) => {set({user})},
    setAutoChat: (autoChat:boolean) => {set({autoChat})},
    setFileChat: (fileChat:boolean) => {set({fileChat})},
    setIsMultiple: (isMultiple:boolean) => {set({isMultiple})},
    clear: () => {set({notifications:[]})},
    toggleTheme: () => {set({theme:'dark'})},
    setMouseOverPath: (mouseOverPath: string) => {set({mouseOverPath})},
    setSelectedPath: (selectedPath: string) => {set({selectedPath})},
    SetSelectedText: (selectedText: string) => {set({selectedText})},
    setSelectedChat: (selectedChat: string) => {set({selectedChat})},
    setInspireResult: (inspireResult:  JSONContent | string | null) =>  {set({inspireResult})},
    setToolActionName: (toolActionName: string) => { set({toolActionName})},
    setActiveToolItem: (acitiveItem: FlowTask) => { set({activeToolItem: acitiveItem})},
    setWorkflow: (workflow: string) => {set({workflow})},
    setMessagesMode: (messagesMode: string) => { set({messagesMode})},
    setJsonBlock: (jsonBlock: JsonBlock) => {set({jsonBlock})},
    setMessageSugguestionJson: (messageSugguestionJson: JSONContent|string|null) => {
      console.log('useGlobalStore setMessageSugguestionJson => :', messageSugguestionJson);
      console.log('useGlobalStore setMessageSugguestionJson get => :', get().messageSugguestionJson);

      set({messageSugguestionJson})
    },
    llog: (message: string) => {
      console.log('llog.store message', message);
      // try {
      //     set({notification: message});
      //     const { notifications } = useGlobalStore.getState();
      //     notifications.push({ id: Date.now().toString(), message });    
      // } catch (error) {}
    },


    // --- 新的任务管理 Actions 实现 ---

    /**
     * 统一的任务添加入口。
     * 根据任务类型自动分发到并行队列或顺序队列。
     */
    addTask: (task) => {
      if (!task.name?.trim()|| !task.type || task.prompt?.length === 0) {
        toast.error('任务名称或prompt不能为空');
        return; // 不修改状态
      }

      const parallelizableTypes: TaskType[] = ['parallel', 'inspire', 'suguesstions', 'image', 'video'];
      const isParallel = parallelizableTypes.includes(task.type) || task.notDependPrevious;
      const PARALLEL_LIMIT = 15;

      if (isParallel && get().parallelTasks.length < PARALLEL_LIMIT) {
        // 分发到并行队列
        const newTask: Task = {
          ...task,
          id: nanoid(),
          status: 'pending', // 初始状态为等待执行
          createdAt: new Date().toISOString(),
        };

        set(state => {{
          // 检查重复任务（根据业务需求可选）
          const exists = state.parallelTasks.some(
            t =>  t.prompt === task.prompt
          );
          if (exists) {
            toast.error('任务已存在');
            return state;
          }
          return {
            notification: '已添加新任务:' + task.prompt,
            parallelTasks: [...state.parallelTasks, newTask],
            // newTask: task // 现在类型合法
          };
        }});
      } else {

        // 分发到顺序队列 (包括并行队列已满的情况)
        if (isParallel) {
            toast.warning(`并行队列已满，任务 "${task.name}" 将排队等候`);
        }

        const newTask: Task = {
            ...task,
            id: nanoid(),
            status: 'pending', // 顺序任务也用 pending 状态
            createdAt: new Date().toISOString(),
        };
        // set(state => ({
        //   sequentialTasks: [...state.sequentialTasks, newTask]
        // }));


        set(state => {
          // newTaskList: [...state.newTaskList, newTask]
          if (!task.name?.trim()|| !task.type || task.prompt?.length === 0) {
            toast.error('任务名称或prompt不能为空');
            return state; // 不修改状态
          }
          // 检查重复任务（根据业务需求可选）
          const exists = state.newTaskList.some(
            t => t.prompt === task.prompt // || t.title === task.title || 
          );
          if (exists) {
            toast.error('任务已存在');
            return state;
          }

          // return {
          //   ...state,
          //   notification: '添加新任务:' + task.prompt,
          //   newTaskList: [...state.newTaskList, newTask],
          //   newTask: task // 现在类型合法
          // };

          return {
            newTaskList: [...state.newTaskList, newTask],
          };
        });
      }
    },

    /**
     * 更新指定ID的并行任务的属性（如 status, result, error）。
     */
    updateParallelTask: (taskId, updates) => {
      set(state => ({
        parallelTasks: state.parallelTasks.map(task =>
          task.id === taskId ? { ...task, ...updates } : task
        )
      }));
    },

    /**
     * 从并行任务队列中移除一个任务（通常在任务完成并归并后调用）。
     */
    removeParallelTask: (taskId) => {
      set(state => ({
        parallelTasks: state.parallelTasks.filter(task => task.id !== taskId)
      }));
    },



    /**
     * 获取顺序队列中的第一个任务，但不移除它。
     */
    getFirstSequentialTask: () => {
        return get().sequentialTasks[0];
    },

    /**
     * 移除顺序队列中的第一个任务。
     */
    removeFirstSequentialTask: () => {
        set(state => ({
            sequentialTasks: state.sequentialTasks.slice(1)
        }));
    },
    /**
     * 新增：根据提供的排序后列表，更新顺序任务队列。
     * 这允许用户通过拖拽等UI操作重新安排等待执行的任务。
     */
    updateSequentialTaskOrder: (orderedTasks) => {
      // 为了安全，我们可以验证传入的列表是否包含所有原始任务
      const currentTasks = get().sequentialTasks;
      if (orderedTasks.length !== currentTasks.length) {
        console.error("排序失败：任务数量不匹配。");
        toast.error("任务排序失败");
        return;
      }
      
      // 直接用排序后的新列表覆盖旧列表
      set({ sequentialTasks: orderedTasks });
      toast.success("任务顺序已更新");
    },
    // --- 旧的任务管理 Actions (建议废弃或移除) ---
    /* 
      下面的 actions (addTasks, removeTask, setNewTaskList, addNewTask, updateNewTaskListOrder)
      是基于旧的单一 newTaskList 设计的。
      在新的并行/顺序队列模型下，它们的功能已被新的 addTask 等 actions 取代。
      建议在整个应用中将对它们的调用替换为对新 action 的调用，然后将它们移除。
    */

    // 在zustand store中优化后的代码
    addTasks: (tasks: Task[]) => {
      set(state => ({
        newTaskList: [
          ...state.newTaskList,
          ...tasks.map(t => ({
            ...t,
            id: t.id || nanoid(),
            createdAt: t.createdAt || new Date().toISOString()
          }))
        ]
      }));
    },
    removeTask: (taskId: string) => {
      set(state => ({
        newTaskList: state.newTaskList.filter(task => task.id !== taskId)
      }));
    },
    // 批量更新任务列表
    updateTasks: (tasks: Task[]) => {
    },
    // 直接设置新任务列表
    setNewTaskList: (tasks: Task[]) => {
      set({
        newTaskList: tasks.map(t => ({
          ...t,
          id: t.id || nanoid(),
          createdAt: t.createdAt || new Date().toISOString()
        }))
      });
    },
    addNewTask: (task: Task) => {
      // 添加任务验证函数
      const validateTask = (task: Partial<Task>): task is Task => {
        return !!task.name?.trim() && !!task.type;
      };

      // 使用immer优化嵌套更新
      // npm install immer
      // import { produce } from 'immer';

      // // 在set回调中使用
      // set(produce(state => {
      //   if (!validateTask(task)) return;
      //   state.newTaskList.push({...task, id: crypto.randomUUID()});
      //   state.newTask = null;
      // }));

      try {
        // 生成唯一ID（推荐使用nanoid库）
        const taskId = task.id || nanoid() || crypto.randomUUID(); // 或 Date.now().toString()  // 使用函数式更新确保原子性操作
        set((state) => { // 验证任务必须字段
          if (!task.name?.trim()|| !task.type || task.prompt?.length === 0) {
            toast.error('任务名称或prompt不能为空');
            return state; // 不修改状态
          }
          // 检查重复任务（根据业务需求可选）
          const exists = state.newTaskList.some(
            t =>  t.prompt === task.prompt // t.title === task.title ||
          );
          if (exists) {
            toast.error('任务已存在');
            return state;
          }

          return {
            ...state,
            notification: 'Add new task:' + task.prompt,
            newTaskList: [
              ...state.newTaskList,
              {
                ...task,
                id: taskId,
                createdAt: new Date().toISOString()
              }
            ],
            newTask: task // 现在类型合法
          };
        });
                        
        // 可选：持久化到本地存储
        // toast.success('已添加任务：' + task.name);

      } catch (error) {
        console.error('添加任务失败:', error);
        toast.error(`添加失败: ${error instanceof Error ? error.message : '未知错误'}`);
      }
    },
    updateNewTaskListOrder: (order: {name:string, id:string, title?:string, index:number}[]) => {
      const { newTaskList } = useGlobalStore.getState();
      // 更多优化参考：https://chat.deepseek.com/a/chat/s/5ce3d740-643a-4789-9f9c-8deab8ecef30
      // 创建任务映射表（使用name作为唯一标识）
      console.log(`updateNewTaskListOrder order:`, order)

      const taskMap = new Map(
        newTaskList.map(task => [task.id, task])
      );
      console.log(`updateNewTaskListOrder taskMap:`, taskMap)

      // 按order顺序重新排列任务
      const orderedTasks = order
        .map(({ id }) => taskMap.get(id))
        .filter((task): task is Task => !!task) // 过滤无效项
        console.log(`updateNewTaskListOrder orderedTasks:`, orderedTasks)

      // 保留未在order中的任务（追加到末尾）
      const remainingTasks = newTaskList.filter(
        task => !order.some(o => o.id === task.id)
      );
      console.log(`updateNewTaskListOrder remainingTasks:`, remainingTasks)


      // 更新store状态
      console.log(`updateNewTaskListOrder:`, [...orderedTasks, ...remainingTasks])
      set({
        newTaskList: [...orderedTasks, ...remainingTasks]
      });
    },
  }),
  {
    name: 'global-store',
    storage: createJSONStorage<Partial<GlobalState>>(), // 关键修改点
    partialize: (state) => ({
      messagesMode: state.messagesMode,
      // 添加其他需要持久化的属性（至少包含所有必需属性）
      theme: state.theme,
      enabled: state.enabled,
      user: state.user,
      toolActionName: state.toolActionName,
      activeToolItem: state.activeToolItem,
      autoChat: state.autoChat,
      fileChat: state.fileChat,
      // aiSuggestions: state.aiSuggestions,
      mouseOverPath: state.mouseOverPath,
      selectedPath: state.selectedPath,
      selectedChat: state.selectedChat,
      processEnv: state.processEnv,
      aiKeys: state.aiKeys,
      currentLink: state.currentLink,
      sequentialTasks: state.sequentialTasks,
      parallelTasks: state.parallelTasks, 
      newTaskList: state.newTaskList,
      messageSugguestionJson:  state.messageSugguestionJson, // 持久化最近的灵感创意数据
      // ...其他需要持久化的属性
    } as GlobalState), // 类型断言确保符合接口
    version: 1,
    migrate: (persistedState, version) => {
      if (version === 0 && persistedState ) { // 版本迁移示例
        // const { newTaskList = [] } = persistedState;

        // if (persistedState && typeof persistedState === 'object' && 'newTaskList' in persistedState) {
        //   const { newTaskList = [] } = persistedState as { newTaskList?: any[] };
        //   // 使用 newTaskList
        // } else {
        //   const newTaskList = [];
        //   // 处理 newTaskList 不存在的情况
        // }

        const { newTaskList = [] } = persistedState as { newTaskList?: any[] };

        if (newTaskList.length > 0) {
          return {
            ...persistedState,
            newTaskList: newTaskList.map((t: any) => ({
              ...t,
              createdAt: t.createdAt || new Date().toISOString()
            }))
          }
        }
      }
      return persistedState as GlobalState
    }
  }),
  {
    name: 'GlobalStore',
    enabled: process.env.NODE_ENV === 'development',
    store: 'globalStore' // 在DevTools中显示的名称
  })
);

export default  useGlobalStore // 所有使用的组件都共享一个store


// 注册到全局
// registerStore('globalStore', useGlobalStore);

// From: https://chat.deepseek.com/a/chat/s/5ce3d740-643a-4789-9f9c-8deab8ecef30

// 控制台直接访问store状态
// window.store = useGlobalStore;
// // 在控制台输入 store.getState().newTaskList 查看实时状态

// 查看所有注册的store
// window.__stores__

// 获取全局store的当前状态
// window.__stores__.globalStore.getState()

// 直接调用action
// window.__stores__.globalStore.getState().setNewTaskList([])

// user-store.ts
// const useUserStore = create<UserState>()(
//   devtools(
//     (set) => ({/*...*/}),
//     { name: 'UserStore' }
//   )
// );
// registerStore('userStore', useUserStore);

// 在组件中访问
// const userStore = useUserStore();
// 生产环境安全：
// // 在打包时自动移除调试代码
// if (process.env.NODE_ENV === 'production') {
//   registerStore = () => {}; // 空函数
// }





// // 完善store注册类型
// type StoreNames = 'globalStore' | 'userStore'; 

// declare global {
//   interface Window {
//     __stores__: {
//       [K in StoreNames]?: typeof useGlobalStore | typeof useUserStore;
//     };
//   }
// }

// 性能优化：
// 使用浅比较选择器避免重复渲染
// const taskList = useGlobalStore(
//   state => state.newTaskList,
//   shallow
// );

// 监控状态变化
// window.__stores__.globalStore.subscribe(console.log)

// 执行时间旅行调试
// window.__stores__.globalStore.setState(previousState)



export const testSugguestions = [
    'What are 1',
    `Write code to 2`,
    `Help me 3`,
    'What is 4',
    `Help me 5`,
    'What is 6',
    'What are 7',
    `Write code to 8`,
    'What are 9',
    `Write code to 10`,
  ]



export const defaultAISuggestions = {
    "进一步改进优化": [
        "建议1 23323223",
        "建议23232wee额额外we",
        "建议3 万恶的服务费俄文",
        "其他更多改进优化建议或方向描述"
      ],
      "继续探讨": [
        "问题1",
        "问题2",
        "问题3",
        "其他更多预测用户想问的问题"
      ],
      "关联延伸": [
        "知识1",
        "知识2",
        "主题1",
        "主题2", "其他更多可延伸扩展的相关性知识或主题，其他更多可延伸扩展的相"
      ],
      "进一步改进优化3": [
        "建议1 23323223",
        "建议23232wee额额外we",
        "建议3 万恶的服务费俄文",
        "其他更多改进优化建议或方向描述"
      ],
      "继续探讨3": [
        "问题1",
        "问题2",
        "问题3",
        "其他更多预测用户想问的问题"
      ],
      "关联延伸2": [
        "知识1",
        "知识2",
        "主题1",
        "主题2", "其他更多可延伸扩展的相关性知识或主题，其他更多可延伸扩展的相"
      ],
  }

  

export const defaultAISuggestions1 = {
	"进一步改进优化": [
      "建议1",
      "建议2",
      "建议3",
      "其他更多改进优化建议或方向描述，都以一句话字符串形式列在当前数组"
    ],
    "继续探讨": [
      "问题1",
      "问题2",
      "问题3",
      "其他更多预测用户想问的问题，也都以一句话字符串形式列在数组中"
    ],
    "关联延伸": [
      "知识1",
      "知识2",
      "主题1",
      "主题2", "其他更多可延伸扩展的相关性知识或主题，也都以一句话字符串形式列在数组中"
    ],
}


export  const  defaultMessageSugguestionJson = {
    "爆款选题": [
      "🌿清明节背后的文化密码｜45岁以下女性必看！",
      "💡解读清明节的起源与现代意义｜自媒体人知识笔记",
      "🌸用STAR模型讲述清明节故事｜如何打动人心？",
      "✨清明节文化价值解析｜4步打造有温度的知识内容",
      "🌧️雨中清明：一场关于情感与文化的深度对话"
    ],
    "维度参数": [
      "风格定制: 专业度高, 情感色彩浓, 温暖治愈系, 知识干货型",
      "标签热词: 分享价值, 文化解读, 情感共鸣, 传统节日, 生活美学, 知识科普",
      "内容框架: 故事化叙述：STAR（情境化个人体验）, PAS（问题-激发-解决）, FAB（场景化解决方案）, 六顶思考帽（情感/理性分析）",
      "目标平台: 公众号, 小红书, 微博, 知乎, 抖音文字流",
      "读者定位: 自媒体人群, 45岁以下女性, 知识爱好者, 文艺青年, 职场女性, 创作者群体",
      "输出类型: 知识笔记, 清单型内容, 干货教程, 故事叙述, 对比测评",
      "篇幅长度: 中等1500字内, 长文2000字以上, 短篇精炼800字内",
      "标题元素: 数字, emoji, 问句, 关键词组合",
      "互动引导: 提问互动, 投票选择, 留言区问答, 打卡挑战赛, 福利抽奖",
      "热门标签: 清明节, 文化价值, 自媒体运营, 情感表达, 内容创作, 知识分享, 女性成长",
      "关键词植入: 必看❗️, 亲测有效, 99%人不知道, 学霸级攻略, 高效学习法"
    ],
    "继续探讨": [
      "如何用STAR模型构建清明节相关的故事化内容？",
      "如何在公众号上吸引更多45岁以下女性读者？",
      "有哪些适合清明节的文化活动可以融入内容创作中？",
      "如何通过清明节主题内容提升自媒体账号的专业形象？",
      "清明节的现代意义如何与当代女性价值观结合？"
    ],
    "关联延伸": [
      "清明节的传统习俗及其现代化演变",
      "自媒体内容创作中的情感营销策略",
      "如何利用传统文化元素打造爆款内容",
      "女性视角下的节日文化解读",
      "内容创作者必备的写作技巧与方法论"
    ]
  }


// Assuming defaultMessageSugguestionJson is defined somewhere in your code

// Safely accessing keys with optional chaining and providing default values
// const recommendedTitles = defaultMessageSugguestionJson["爆款选题"] ?? [];
// const dimensionParameters = defaultMessageSugguestionJson["维度参数"] ?? [];
// const furtherDiscussion = defaultMessageSugguestionJson["继续探讨"] ?? [];
// const relatedExtensions = defaultMessageSugguestionJson["关联延伸"] ?? [];

// Example usage
// console.log(recommendedTitles);
// console.log(dimensionParameters);
// console.log(furtherDiscussion);
// console.log(relatedExtensions);

// Extract "维度参数" and create a new object without it
// const { "维度参数": dimensionParameters2, ...restOfJson } = defaultMessageSugguestionJson;

// dimensionParameters2 contains the "维度参数" array
// console.log(dimensionParameters2);

// restOfJson contains all other keys except "维度参数"
// console.log(restOfJson); // Contains "爆款选题", "继续探讨", "关联延伸"


